import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit'; // Importing from `utils` makes them more type-safe ✅
import { JobsState } from './types';
import {useInjectReducer} from "redux-injectors";

// The initial state of the Jobs page
export const initialState: JobsState = {
    jobs: [],
    isLoading: false,
    jobsByTab: {},
    loadingByTab: {},
    lastFetchByTab: {},
    // Recent Jobs Sidepanel initial state
    isPanelOpen: false,
    selectedTab: '',
    searchQuery: '',
    isPolling: false,
};

const slice = createSlice({
    name: 'jobs',
    initialState,
    reducers: {
        updateJobs(state, action: PayloadAction<any>) {
            // Here we say lets change the username in my homepage state when changeUsername actions fires
            // Type-safe: It will expect `string` when firing the action. ✅
            // @ts-ignore
            state.jobs = action.payload;
        },
        deleteJob(state, action: PayloadAction<any>) {
            console.log(action);
            // @ts-ignore
            state.jobs = state.jobs.filter(item => item.uuid !== action.payload);
        },
        updateJobRatingLocally(state, action: PayloadAction<any>) {
            // @ts-ignore
            state.jobs = state.jobs.map(item => item.uuid === action.payload.uuid ? {...item, rating: action.payload.rating} : item);

            // Also update jobs in all tabs to ensure consistency
            Object.keys(state.jobsByTab).forEach(tabKey => {
                if (state.jobsByTab[tabKey]) {
                    state.jobsByTab[tabKey] = state.jobsByTab[tabKey].map(item =>
                        item.uuid === action.payload.uuid ? {...item, rating: action.payload.rating} : item
                    );
                }
            });
        },
        updateJobIsPublicLocally(state, action: PayloadAction<any>) {
            console.log('updateJobIsPublicLocally', action.payload);
            // @ts-ignore
            state.jobs = state.jobs.map(item => item.uuid === action.payload.uuid ? {...item, is_public: action.payload.is_public} : item);

            // Also update jobs in all tabs to ensure consistency
            Object.keys(state.jobsByTab).forEach(tabKey => {
                if (state.jobsByTab[tabKey]) {
                    state.jobsByTab[tabKey] = state.jobsByTab[tabKey].map(item =>
                        item.uuid === action.payload.uuid ? {...item, is_public: action.payload.is_public} : item
                    );
                }
            });
        },
        addJobToTab(state, action: PayloadAction<{ tabKey: string; job: any }>) {
            const { tabKey, job } = action.payload;
            state.jobsByTab[tabKey] = [job, ...(state.jobsByTab[tabKey] || [])];
            state.lastFetchByTab[tabKey] = Date.now();
        },
        updateJobsForTab(state, action: PayloadAction<{ tabKey: string; jobs: any[] }>) {
            const { tabKey, jobs } = action.payload;
            state.jobsByTab[tabKey] = jobs;
            state.lastFetchByTab[tabKey] = Date.now();
        },
        deleteJobFromTab(state, action: PayloadAction<{ tabKey: string; jobId: string }>) {
            const { tabKey, jobId } = action.payload;
            if (state.jobsByTab[tabKey]) {
                state.jobsByTab[tabKey] = state.jobsByTab[tabKey].filter(item => item.uuid !== jobId);
            }
        },
        updateJobRatingInTab(state, action: PayloadAction<{ tabKey: string; jobId: string; rating: number }>) {
            const { tabKey, jobId, rating } = action.payload;
            if (state.jobsByTab[tabKey]) {
                state.jobsByTab[tabKey] = state.jobsByTab[tabKey].map(item => 
                    item.uuid === jobId ? {...item, rating} : item
                );
            }
        },
        setTabLoading(state, action: PayloadAction<{ tabKey: string; loading: boolean }>) {
            const { tabKey, loading } = action.payload;
            state.loadingByTab[tabKey] = loading;
        },
        clearJobsForTab(state, action: PayloadAction<string>) {
            const tabKey = action.payload;
            delete state.jobsByTab[tabKey];
            delete state.loadingByTab[tabKey];
            delete state.lastFetchByTab[tabKey];
        },
        clearAllJobs(state) {
            state.jobsByTab = {};
            state.loadingByTab = {};
            state.lastFetchByTab = {};
        },
        // New actions for Recent Jobs Sidepanel
        setPanelOpen(state, action: PayloadAction<boolean>) {
            state.isPanelOpen = action.payload;
        },
        setSelectedTab(state, action: PayloadAction<string>) {
            state.selectedTab = action.payload;
        },
        setSearchQuery(state, action: PayloadAction<string>) {
            state.searchQuery = action.payload;
        },
        setPolling(state, action: PayloadAction<boolean>) {
            state.isPolling = action.payload;
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
    },
});

/**
 * Let's turn this into a hook style usage. This will inject the slice to redux store and return actions in case you want to use in the component
 */
export const useJobsSlice = () => {
    useInjectReducer({ key: slice.name, reducer: slice.reducer });
    return { actions: slice.actions };
};
